import {Modal} from "antd";
import React, {ReactNode} from "react";
import {connect} from "react-redux";
import {useNavigate} from "react-router-dom";
import {bindActionCreators, Dispatch} from "redux";
import {apiErrorReset} from "../../../core/app/apiError/actions";
import {ErrorDto} from "../../../core/models/dtos/error.dto";
import FirebaseService from "../../../core/services/firebase.service";
import ContentKeys from "../../../core/utilities/contentKeys";
import {Errors} from "../../../core/utilities/errors";
import {Images} from "../../../core/utilities/images";
import {IStore} from "../../../core/utilities/reducers";
import {router} from "../../../core/utilities/router";
import {UI} from "../../../core/utilities/ui";
import AppButton from "../../Buttons/AppButton/AppButton";
import ContentKey from "../../ContentKey/ContentKey";
import "./ApiErrorModal.scss";

interface IProps {
  apiError?: ErrorDto;
  pathname: string;
  apiErrorReset: () => void;
  callbackForQuestionPageRetry?: () => void;
  callbackForQuestionPageHome?: () => void;
}

const ApiErrorModal = (props: IProps) => {
  const navigate = useNavigate();
  
  const isVisible = (): boolean => {
    const error = props.apiError;
    if (error) {
      return !(
        Errors.isAuthError(error) ||
        Errors.isContactUsError(error) ||
        Errors.isPaymentLogError(error)
      );
    }
    return false;
  }
  
  const handleError = (): void => {
    props.apiErrorReset();
    navigate(router.HOME);
  }
  
  const renderGenericError = (): ReactNode => {
    return (
      <React.Fragment>
        <img className="popup-icon error" src={Images.popup_icon_warning} alt="popupIcon"/>
        <span className="title link-medium">{Errors.errorGenericTitle}</span>
        <span className="text text-medium">{Errors.errorGenericText}</span>
        <AppButton
          text="Tamam"
          onClick={handleError}
        />
      </React.Fragment>
    );
  }
  
  const renderGenericErrorForQuestion = (): ReactNode => {
    if (!props.callbackForQuestionPageRetry || !props.callbackForQuestionPageHome) {
      alert('callbacks not defined for error');
      navigate(router.HOME);
      return;
    }
    return (
      <React.Fragment>
        <img className="popup-icon error" src={Images.popup_icon_warning} alt="popupIcon"/>
        <ContentKey
          className="title link-medium"
          contentKey={ContentKeys.GENERAL_ERROR_TITLE}
        />
        <ContentKey
          className="text text-medium"
          contentKey={ContentKeys.GENERAL_ERROR_DESC}
        />
        <AppButton
          text={<ContentKey contentKey={ContentKeys.GENERAL_ERROR_RETRY_BUTTON}/>}
          onClick={() => props.callbackForQuestionPageRetry!()}
        />
        <AppButton
          className="home-button"
          text={<ContentKey contentKey={ContentKeys.GENERAL_ERROR_HOME_BUTTON}/>}
          onClick={() => props.callbackForQuestionPageHome!()}
        />
      </React.Fragment>
    );
  }
  
  const renderDeviceError = (): ReactNode => {
    return (
      <React.Fragment>
        <img className="popup-icon error" src={Images.popup_icon_warning} alt="popupIcon"/>
        <ContentKey
          className="title link-medium"
          contentKey={ContentKeys.MODAL_SESSION_ERROR_TITLE}
        />
        <AppButton
          text={<ContentKey contentKey={ContentKeys.MODAL_SESSION_ERROR_BUTTON}/>}
          onClick={handleError}
        />
      </React.Fragment>
    );
  }
  
  const renderPaymentError = (error: ErrorDto): ReactNode => {
    if (!props.callbackForQuestionPageRetry || !props.callbackForQuestionPageHome) {
      alert('callbacks not defined for error');
      navigate(router.HOME);
      return;
    }
    const isGeneralError = error.message === Errors.errorMessagePaymentGeneralError;
    return (
      <React.Fragment>
        <img className="popup-icon fund" src={Images.popup_icon_fund} alt="popupIcon"/>
        <ContentKey
          className="title link-medium"
          contentKey={FirebaseService.PAYMENT_GENERAL_ERROR_TITLE}
        />
        <ContentKey
          className="text text-medium"
          contentKey={FirebaseService.PAYMENT_GENERAL_ERROR_DESC}
        />
        {
          isGeneralError
            ?
            <React.Fragment>
              <AppButton
                text={<ContentKey contentKey={FirebaseService.PAYMENT_GENERAL_ERROR_RETRY_BUTTON}/>}
                onClick={() => props.callbackForQuestionPageRetry!()}
              />
              <AppButton
                className="home-button"
                text={<ContentKey contentKey={FirebaseService.PAYMENT_GENERAL_ERROR_HOME_BUTTON}/>}
                onClick={() => props.callbackForQuestionPageHome!()}
              />
            </React.Fragment>
            :
            <AppButton
              text={<ContentKey contentKey={FirebaseService.PAYMENT_GENERAL_ERROR_HOME_BUTTON}/>}
              onClick={() => props.callbackForQuestionPageHome!()}
            />
        }
      </React.Fragment>
    );
  }
  
  const renderError = (): ReactNode => {
    const apiError = props.apiError;
    
    if (apiError && Errors.isDeviceError(apiError)) {
      return renderDeviceError();
    }
    if (apiError && Errors.isPaymentError(apiError)) {
      return renderPaymentError(apiError);
    }
    if (props.pathname === router.QUESTION) {
      return renderGenericErrorForQuestion();
    }
    return renderGenericError();
  }
  
  return (
    <Modal
      className="api-error-modal"
      maskStyle={UI.modalMaskStyle()}
      open={isVisible()}
      closable={false}
      footer={false}
      centered
      destroyOnClose
    >
      <div className="body">
        {isVisible() && renderError()}
      </div>
    </Modal>
  );
}

const mapDispatchToProps = (dispatch: Dispatch) => {
  return bindActionCreators(
    {
      apiErrorReset,
    },
    dispatch
  );
};
const mapStateToProps = (store: IStore) => {
  return {
    apiError: store.apiError.error,
    pathname: store.setPathname.pathname,
  }
};

export default connect(mapStateToProps, mapDispatchToProps)(ApiErrorModal);
